<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ElNotification } from 'element-plus';
import { Add as Plus, NewMessage, DeleteOutlined, Done } from '@xpos/icons-svg';
import languageFormSchema from '@/forms/language.json';
import SchemaForm from '@xpos/ui/src/components/SchemaForm/index.vue';
const { t } = useI18n();
const $emit = defineEmits<{
  (event: 'change', value: boolean): void;
  (event: 'update', value: boolean): void;
  (event: 'update:modelValue', value: boolean): void;
  (event: 'close'): void;
  (event: 'save', value: Record<string, string>): void;
}>();

const $props = defineProps<{
  modelValue: boolean;
  title?: string;
  config?: Record<string, string>;
  modal?: boolean;
}>();

const visible = ref($props.modelValue);
const showAddLanguageDialog = ref(false);
const formData = ref<Record<string, string>>(typeof $props.config === 'object' ? $props.config : {});
const newForm = ref<{ key?: string; value?: string }>({});

const LANGUAGE = {
  en: t('language.enTranslation'),
  zh: t('language.zhTranslation'),
  th: t('language.thTranslation'),
  vi: t('language.viTranslation'),
  id: t('language.idTranslation'),
  ko: t('language.koTranslation'),
};

let editKey: string = '';
const handleEdit = (key: string) => {
  editKey = key;
  showAddLanguageDialog.value = true;
  newForm.value = { key, value: formData.value[key] };
};
const handleDelete = (key: string) => {
  delete formData.value[key];
};

const schemaForm$ref = ref();
const handleCreate = async () => {
  // Block empty language/translation so name_translations never stores blank values
  if (!newForm.value.key || !newForm.value.value?.trim())
    return ElNotification({ customClass: 'x-notification__error', title: t('common.error'), message: !newForm.value.key ? t('language.name.description') : t('language.translation.description') });
  schemaForm$ref.value.formRef.$$uiFormRef.validate().then(async (valid: boolean) => {
    if (!valid) {
      return;
    }

    if (editKey !== '' && editKey != newForm.value.key) {
      delete formData.value[editKey];
    }

    //@ts-ignore
    formData.value[newForm.value!.key] = newForm.value.value.trim();

    editKey = '';

    showAddLanguageDialog.value = false;
  }).catch(() => {});
};
const handleCreate2 = () => {
  $emit('save', formData.value);
  $emit('change', false);
  $emit('update:modelValue', false);
  $emit('close');
};

watch(
  () => $props.config,
  (newVal) => {
    formData.value = (newVal as Record<string, string>) || {};
  },
);

watch(
  () => $props.modelValue,
  (newVal: boolean) => {
    visible.value = newVal;
  },
);
const handleClose = () => {
  $emit('change', false);
  $emit('update:modelValue', false);
  $emit('close');
};
</script>

<template>
  <x-dialog
    v-model="visible"
    width="770px"
    :title="$props.title"
    :before-close="handleClose"
    @close="handleClose"
    :close-on-click-modal="false"
    :append-to-body="true"
    :modal="$props.modal"
  >
    <template #headerRight>
      <el-button @click="handleCreate2" color="#80BA5C"
        ><el-icon size="20" color="#fff"><Done /></el-icon
      ></el-button>
    </template>
    <div class="w-full">
      <div class="flex w-full gap-[5px]" v-for="(value, key) in formData">
        <x-item :border="true" class="x-item grow">
          <div class="leading-4">
            <div>{{ LANGUAGE[key as 'zh' | 'en' | 'th' | 'vi' | 'id' | 'ko'] }}</div>
            <!-- <div><small>Upload a product picture</small></div> -->
          </div>
          <template #end>
            <div class="truncate max-w-[350px]">{{ value }}</div>
          </template>
        </x-item>
        <el-space :size="5">
          <x-button type="primary" class="button-square" @click="handleEdit(key)"
            ><el-icon size="20"> <NewMessage /> </el-icon
          ></x-button>
          <x-button color="#E53030" class="button-square" @click="handleDelete(key)"
            ><el-icon size="20"> <DeleteOutlined /> </el-icon
          ></x-button>
        </el-space>
      </div>
      <div class="text-center mb-[5px]">
        <el-button
          type="primary"
          class="!leading-[50px]"
          @click="(showAddLanguageDialog = true), (newForm = { key: '', value: '' })"
          ><el-icon size="20" class="align-sub mr-2"><Plus /></el-icon>{{ t('language.addNew') }}</el-button
        >
      </div>
    </div>
  </x-dialog>
  <x-dialog
    v-model="showAddLanguageDialog"
    width="770px"
    :title="$props.title"
    :close-on-click-modal="false"
    :append-to-body="true"
  >
    <template #headerRight>
      <el-button @click="handleCreate" color="#80BA5C"
        ><el-icon size="20" color="#fff"><Done /></el-icon
      ></el-button>
    </template>
    <div class="w-full">
      <SchemaForm :schema="languageFormSchema" ref="schemaForm$ref" v-model="newForm" />
    </div>
  </x-dialog>
</template>
